import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { IconDotDivider } from '../../../../../../design-system/components/icons/DotDivider';
import { NavMenuButton } from '../../../../../../design-system/components/nav/nav-menu-button/NavMenuButton';
import { NavMenuImageCard } from '../../../../../../design-system/components/nav/nav-menu-image-card/NavMenuImageCard';
import { RATIO } from '../../../../../../design-system/components/ratio/Ratio';
import { NormalizedWhereToGoMenu } from '../../../../../../resources/whereToGoMenu/api/prismic/normalizers';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { MobileMenuTitle } from '../MobileMenuTitle/MobileMenuTitle';

import style from './WhenView.module.css';

const MAX_VISIBLE_THEMATICS = 12;

export function WhenView({
    when,
    title,
}: {
    when: NonNullable<NonNullable<NormalizedWhereToGoMenu>['when']>;
    title: string;
}) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    const [firstHighlightedThematic, ...otherHighlightedThematics] = when.thematicsHighlight;

    return (
        <div>
            <div>
                <MobileMenuTitle title={title} />
            </div>

            {when.thematicsHighlight.length > 0 ? (
                <div className={style.menuImageContainer}>
                    <div className={style.menuImageFirstHighlightedWrapper}>
                        <NavMenuImageCard
                            label={firstHighlightedThematic.label}
                            blur={true}
                            aspectRatio={RATIO['10/3']}
                            href={generatePath('hub_thematic', {
                                thematicSlug: firstHighlightedThematic.hubpage.slug,
                            })}
                            image={{
                                alt: firstHighlightedThematic.hubpage.coverPicture.card_cover_mobile
                                    .alt,
                                src: firstHighlightedThematic.hubpage.coverPicture.card_cover_mobile
                                    .url,
                                loading: 'lazy',
                                unoptimized: true,
                            }}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.primaryNavThematicClicked({
                                        ...defaultProperties,
                                        block: 'primary_nav',
                                        card_number: 1,
                                        row_number: 2,
                                        thematic_slug: firstHighlightedThematic.hubpage.slug,
                                        click_type: 'card',
                                        column_number: 0,
                                        thematic_group: 'when',
                                    });
                                });
                            }}
                        />
                    </div>

                    {otherHighlightedThematics.length > 0 ? (
                        <div className={style.menuImageOtherHighlightedWrapper}>
                            {otherHighlightedThematics
                                .slice(0, 3)
                                .map((thematicHighlight, index) => {
                                    return (
                                        <NavMenuImageCard
                                            key={thematicHighlight.hubpage.id}
                                            label={thematicHighlight.label}
                                            blur={true}
                                            href={generatePath('hub_thematic', {
                                                thematicSlug: thematicHighlight.hubpage.slug,
                                            })}
                                            aspectRatio={RATIO['1/1']}
                                            image={{
                                                alt: thematicHighlight.hubpage.coverPicture
                                                    .card_cover_mobile.alt,
                                                src: thematicHighlight.hubpage.coverPicture
                                                    .card_cover_mobile.url,
                                                loading: 'lazy',
                                                unoptimized: true,
                                            }}
                                            onClick={() => {
                                                trackEvent((ampli, defaultProperties) => {
                                                    ampli.primaryNavThematicClicked({
                                                        ...defaultProperties,
                                                        block: 'primary_nav',
                                                        card_number: index + 2,
                                                        row_number: 2,
                                                        thematic_slug:
                                                            thematicHighlight.hubpage.slug,
                                                        click_type: 'card',
                                                        column_number: 0,
                                                        thematic_group: 'when',
                                                    });
                                                });
                                            }}
                                        />
                                    );
                                })}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {when.thematics.length > 0 ? (
                <div className={style.thematicLinkContainer}>
                    {when.thematics.slice(0, MAX_VISIBLE_THEMATICS).map((thematic, index) => {
                        const indexNumber = index + 1;
                        return (
                            <div className={style.blockDivider} key={thematic.hubpage.id}>
                                <NavMenuButton
                                    href={generatePath('hub_thematic', {
                                        thematicSlug: thematic.hubpage.slug,
                                    })}
                                    onClick={() => {
                                        trackEvent((ampli, defaultProperties) => {
                                            ampli.primaryNavThematicClicked({
                                                ...defaultProperties,
                                                block: 'primary_nav',
                                                card_number: index + 1,
                                                row_number: 2,
                                                thematic_slug: thematic.hubpage.slug,
                                                click_type: 'link',
                                                column_number: 0,
                                                thematic_group: 'when',
                                            });
                                        });
                                    }}
                                >
                                    {thematic.label}
                                </NavMenuButton>

                                {indexNumber % 3 !== 0 && indexNumber !== when.thematics.length ? (
                                    <IconDotDivider
                                        className={style.divider}
                                        color="var(--color-yellow-200)"
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            ) : null}

            <div className={style.footer}>
                <Button
                    priority="secondary"
                    reversed
                    href={generatePath('where_to_go')}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.primaryNavWheretogoClicked({
                                ...defaultProperties,
                                block: 'primary_nav',
                            });
                        });
                    }}
                >
                    {formatMessage({
                        id: 'global.header.globalNav.new.whereToGo.cta',
                    })}
                </Button>
            </div>
        </div>
    );
}
